import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { TICKER_PLACES, DESTINATIONS } from '@/data/destinations'
import { fetchImages, type Img } from '@/services/images'

// Signature destinations used to seed the floating photo cards — one per
// family DNA type keeps a nice spread of styles. Positions tuned for both
// 390px (stacked/clipped gracefully) and 1280px (full spread) viewports.
const SIGNATURE_CARDS = [
  { dest: DESTINATIONS['🎢'][0], style: { top: '8%', left: '4%', rotate: -4, delay: 0 } },
  { dest: DESTINATIONS['🦁'][0], style: { top: '6%', left: undefined, right: '5%', rotate: 3, delay: 0.12 } },
  { dest: DESTINATIONS['🏖'][0], style: { top: '62%', left: '3%', rotate: 2, delay: 0.24 } },
  { dest: DESTINATIONS['🏯'][0], style: { top: '64%', left: undefined, right: '4%', rotate: -3, delay: 0.36 } },
]

interface FloatingCard {
  id: string
  name: string
  tag: string
  style: { top: string; left?: string; right?: string; rotate: number; delay: number }
  image: Img | null
}

export function WelcomeScreen() {
  const setScreen = useAppStore((s) => s.setScreen)
  const [cards, setCards] = useState<FloatingCard[]>(
    SIGNATURE_CARDS.map(({ dest, style }) => ({
      id: dest.id,
      name: dest.name,
      tag: dest.ageRange,
      style,
      image: null,
    }))
  )

  const tickerContent = [...TICKER_PLACES, ...TICKER_PLACES]

  // Fetch real destination photography for the floating cards. If the
  // worker isn't configured (local dev) fetchImages resolves to [] and each
  // card silently keeps its emoji/gradient fallback — no broken images, no
  // layout jump since the card frame is a fixed size either way.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const results = await Promise.all(
        SIGNATURE_CARDS.map(({ dest }) => fetchImages(dest.imageQuery, 1))
      )
      if (cancelled) return
      setCards((prev) =>
        prev.map((c, i) => ({ ...c, image: results[i]?.[0] ?? null }))
      )
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleEnter = () => setScreen('mode')

  return (
    <div
      className="yonder-world min-h-screen flex flex-col cursor-pointer select-none"
      onClick={handleEnter}
      role="button"
      tabIndex={0}
      aria-label="Touch anywhere to begin"
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleEnter()}
    >
      <Atmosphere density="medium" />

      {/* Floating destination photo cards */}
      <div className="relative flex-1 min-h-[520px] sm:min-h-screen">
        {cards.map((c, i) => (
          <motion.div
            key={c.id}
            className="absolute bg-white/95 rounded-2xl overflow-hidden shadow-glow-sun w-28 sm:w-40 z-10"
            style={{ top: c.style.top, left: c.style.left, right: c.style.right }}
            initial={{ opacity: 0, y: 12, rotate: c.style.rotate }}
            animate={{ opacity: 1, y: [0, -8, 0], rotate: c.style.rotate }}
            transition={{
              opacity: { delay: c.style.delay, duration: 0.6, ease: 'backOut' },
              y: { delay: c.style.delay + 0.5, duration: 4 + i * 0.3, repeat: Infinity, ease: 'easeInOut' },
            }}
          >
            {c.image ? (
              <div className="relative w-full h-16 sm:h-24">
                <img
                  src={c.image.thumb || c.image.url}
                  alt={c.image.alt || c.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                    setCards((prev) =>
                      prev.map((p) => (p.id === c.id ? { ...p, image: null } : p))
                    )
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent px-2 py-1.5">
                  <div className="text-[9px] sm:text-[10px] font-black text-white leading-none truncate uppercase tracking-wide">
                    {c.name}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className="text-xl">🧭</span>
                <div>
                  <div className="text-xs font-black text-ink leading-none">{c.name}</div>
                  <div className="text-[9px] font-semibold text-ink-3 mt-0.5">{c.tag}</div>
                </div>
              </div>
            )}
          </motion.div>
        ))}

        {/* Centre wordmark + copy + pulsing CTA pill */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-20">
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-[10px] font-black tracking-[0.3em] text-sun-pale uppercase mb-3"
          >
            by Imaginarium · Family Edition
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-black text-white text-5xl sm:text-7xl leading-[0.95] tracking-tight mb-4"
            style={{ textShadow: '0 4px 40px rgba(255,149,0,0.35)' }}
          >
            Yon<span className="text-sun-pale">der</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-white/85 font-semibold text-base sm:text-lg mb-1 max-w-md"
          >
            Where will your family <span className="italic text-sun-pale font-bold">really</span> belong?
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="text-white/60 font-semibold text-xs sm:text-sm mb-8"
          >
            5 fun questions. Your perfect family holiday. Zero guesswork.
          </motion.p>

          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            onClick={(e) => {
              e.stopPropagation()
              handleEnter()
            }}
            className="yonder-pulse-pill inline-flex items-center gap-2.5 border-2 border-sun-pale/70 rounded-full px-6 py-3 bg-white/5 backdrop-blur-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sun-pale" />
            <span className="text-[11px] sm:text-xs font-black tracking-[0.15em] text-sun-pale uppercase">
              Tap Anywhere — Let&rsquo;s Go
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-sun-pale" />
          </motion.button>
        </div>
      </div>

      {/* Ticker strip */}
      <div className="relative z-10 bg-black/20 border-t border-white/10 py-2.5 overflow-hidden backdrop-blur-sm">
        <div className="flex gap-0 ticker-animate whitespace-nowrap">
          {tickerContent.map((place, i) => (
            <span key={i} className="text-[11px] font-black text-white/70 px-5 tracking-wide">
              {place} <span className="text-sun-pale/50">·</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
